import { ConsumableUseContext } from '@hexcrawl/shared-utils';
import type { IConsumableDef, IConsumableEffect, IItemDef } from '@hexcrawl/shared-utils';
import { getAbilityFromCache } from '../services/abilityService';

/**
 * Player-facing one-line summaries of consumable effects, shared by the inventory
 * panel and the combat Use Item popover so both describe an item identically.
 */
type EffectSummarizer = (params: Record<string, string>) => string;

const EFFECT_SUMMARIZERS: Record<string, EffectSummarizer> = {
  restore_hp: (p) => `Restores ${range(p.min, p.max)} HP`,
  restore_mp: (p) => `Restores ${range(p.min, p.max)} MP`,
  temp_armor: (p) => `+${p.amount ?? '?'} armor for ${range(p.durationMin, p.durationMax)} rounds`,
  // Named only once the ability def has been fetched; the popover preloads it, and
  // anywhere else the generic phrasing still reads correctly.
  cast_ability: (p) => {
    const name = p.abilityId ? getAbilityFromCache(p.abilityId)?.name : undefined;
    return name ? `Casts ${name}` : 'Casts a spell';
  },
  grant_move_points: () => 'Doubles your remaining move points',
  teleport: () => 'Travel to any hex on the map',
};

/** The effect type whose `abilityId` param names a spell the item casts. */
const CAST_ABILITY_EFFECT = 'cast_ability';

/** The effect type that needs a destination hex picked before it can run. */
const TELEPORT_EFFECT = 'teleport';

/** "1–6" for a genuine range, "6" when the bounds are equal. */
function range(min?: string, max?: string): string {
  if (min === undefined || max === undefined) return '?';
  return min === max ? min : `${min}–${max}`;
}

function summarizeEffect(effect: IConsumableEffect): string {
  const summarizer = EFFECT_SUMMARIZERS[effect.type];
  // An effect with no summarizer still reads sensibly: "restore hp" beats a blank line.
  return summarizer ? summarizer(effect.params) : effect.type.replace(/_/g, ' ');
}

/** The full effect list of a consumable as one comma-separated phrase. */
export function summarizeConsumable(consumable: IConsumableDef): string {
  return consumable.effects.map(summarizeEffect).join(', ');
}

/**
 * The ability this item casts, or null if it casts nothing. A non-null result means the
 * item needs a target picked before it can be used, exactly as the spell itself would.
 */
export function getCastAbilityId(def: IItemDef): string | null {
  const effect = def.consumable?.effects.find(e => e.type === CAST_ABILITY_EFFECT);
  return effect?.params.abilityId ?? null;
}

/**
 * True when using this item on the map requires a destination hex first. Such an item
 * opens hex-target mode rather than firing on the spot.
 */
export function needsHexTarget(def: IItemDef): boolean {
  return !!def.consumable?.effects.some(e => e.type === TELEPORT_EFFECT);
}

/** Where the item may be used, or null when it works everywhere. */
export function consumableContextLabel(consumable: IConsumableDef): string | null {
  switch (consumable.useContext) {
    case ConsumableUseContext.COMBAT:      return 'Combat only';
    case ConsumableUseContext.EXPLORATION: return 'Map only';
    default:                               return null;
  }
}

/** True when this def can be activated from the map (character sheet). */
export function isUsableOnMap(def: IItemDef): boolean {
  const context = def.consumable?.useContext;
  return context === ConsumableUseContext.ANY || context === ConsumableUseContext.EXPLORATION;
}

/** True when this def can be activated from the combat Use Item popover. */
export function isUsableInCombat(def: IItemDef): boolean {
  const context = def.consumable?.useContext;
  return context === ConsumableUseContext.ANY || context === ConsumableUseContext.COMBAT;
}
